import { addMinutes, format, parse, parseISO, setMilliseconds, setSeconds } from 'date-fns'
import type { SupportedLocale } from '~/types/localization'
import TimeAgo from 'javascript-time-ago'
import fr from 'javascript-time-ago/locale/fr'
import es from 'javascript-time-ago/locale/es'
import en from 'javascript-time-ago/locale/en'
import { CalendarDate, type DateValue } from '@internationalized/date'
import type { MonthIdentifier } from '~/types/calendar'

TimeAgo.addLocale(fr)
TimeAgo.addLocale(en)
TimeAgo.addLocale(es)

export interface DateTimeRange {
  startISO: string
  endISO: string
  startUTC: Date
  endUTC: Date
}

export const toDate = (dateStr: string): Date => {
  return parse(dateStr, 'yyyy-MM-dd HH:mm:ss', new Date())
}

export const parseCalendarDayAsLocal = (iso: string): Date => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d)
}

export const formatISOToDate = (dateTime?: string | null): string => {
  if (!dateTime) return ''
  try {
    return format(parseISO(dateTime), 'yyyy-MM-dd')
  } catch (error) {
    console.error('Invalid ISO string in formatISOToDate:', dateTime, error)
    return dateTime ?? ''
  }
}

export const formatISOToTime = (dateTime?: string | null): string => {
  if (!dateTime) return ''
  try {
    return format(parseISO(dateTime), 'HH:mm:ss')
  } catch (error) {
    console.error('Invalid ISO string in formatISOToTime:', dateTime, error)
    return ''
  }
}

export const formatISOToDateTime = (dateTime?: string | null): string => {
  if (!dateTime) return ''
  try {
    return format(parseISO(dateTime), 'yyyy-MM-dd HH:mm')
  } catch (error) {
    console.error('Invalid ISO string in formatISOToDateTime:', dateTime, error)
    return ''
  }
}

export const formatISOToDateTimeSeconds = (dateTime?: string | null): string => {
  if (!dateTime) return ''
  try {
    return format(parseISO(dateTime), 'yyyy-MM-dd HH:mm:ss')
  } catch (error) {
    console.error('Invalid ISO string in formatISOToDateTimeSeconds:', dateTime, error)
    return ''
  }
}

export const formatTimeHHmm = (date: Date): string => {
  return format(date, 'HH:mm')
}

export const dateToTimeString = (value?: Date | string | number | null): string => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'string' && /^\d{1,2}:\d{2}$/.test(value)) return value
  const d = value instanceof Date ? value : new Date(value)
  return isNaN(d.getTime()) ? '' : format(d, 'HH:mm')
}

export const getTimeAgo = (dateTime: Date, locale: SupportedLocale): string => {
  const timeAgo = new TimeAgo(locale)
  return timeAgo.format(dateTime)
}

export const createCalendarDate = (date: Date): CalendarDate => {
  return new CalendarDate(
    date.getFullYear(),
    date.getMonth() + 1, // CalendarDate months are 1-indexed
    date.getDate()
  )
}

export const calendarRangeToUTCRange = (
  startDate: DateValue,
  endDate: DateValue,
  startTime?: string,
  endTime?: string
): DateTimeRange => {
  const parseTime = (timeStr: string): { hours: number; minutes: number } => {
    const [hours, minutes] = timeStr.split(':').map(Number)
    return { hours: hours || 0, minutes: minutes || 0 }
  }

  const startTimeObj = startTime ? parseTime(startTime) : { hours: 0, minutes: 0 }
  const endTimeObj = endTime ? parseTime(endTime) : { hours: 23, minutes: 59 }

  const startOfDay = new Date(
    startDate.year,
    startDate.month - 1,
    startDate.day,
    startTimeObj.hours,
    startTimeObj.minutes,
    0,
    0
  )
  const endOfDay = new Date(endDate.year, endDate.month - 1, endDate.day, endTimeObj.hours, endTimeObj.minutes, 59, 999)

  return {
    startISO: startOfDay.toISOString(),
    endISO: endOfDay.toISOString(),
    startUTC: startOfDay,
    endUTC: endOfDay
  }
}

export const calendarDateToDisplayDate = (calendarDate: DateValue): Date => {
  return new Date(calendarDate.year, calendarDate.month - 1, calendarDate.day, 12, 0, 0, 0)
}

export const WEEKDAYS_SHORT: string[] = [
  'time.weekdays.short.sun',
  'time.weekdays.short.mon',
  'time.weekdays.short.tue',
  'time.weekdays.short.wed',
  'time.weekdays.short.thu',
  'time.weekdays.short.fri',
  'time.weekdays.short.sat'
]
export const WEEKDAYS_MIN: string[] = [
  'time.weekdays.min.sun',
  'time.weekdays.min.mon',
  'time.weekdays.min.tue',
  'time.weekdays.min.wed',
  'time.weekdays.min.thu',
  'time.weekdays.min.fri',
  'time.weekdays.min.sat'
]
export const WEEKDAYS: string[] = [
  'time.weekdays.long.sun',
  'time.weekdays.long.mon',
  'time.weekdays.long.tue',
  'time.weekdays.long.wed',
  'time.weekdays.long.thu',
  'time.weekdays.long.fri',
  'time.weekdays.long.sat'
]

export const MONTH_NAMES: string[] = [
  'time.months.long.jan',
  'time.months.long.feb',
  'time.months.long.mar',
  'time.months.long.apr',
  'time.months.long.may',
  'time.months.long.jun',
  'time.months.long.jul',
  'time.months.long.aug',
  'time.months.long.sep',
  'time.months.long.oct',
  'time.months.long.nov',
  'time.months.long.dec'
]
export const MONTHS_SHORT: string[] = [
  'time.months.short.jan',
  'time.months.short.feb',
  'time.months.short.mar',
  'time.months.short.apr',
  'time.months.short.may',
  'time.months.short.jun',
  'time.months.short.jul',
  'time.months.short.aug',
  'time.months.short.sep',
  'time.months.short.oct',
  'time.months.short.nov',
  'time.months.short.dec'
]

export const getFormattedAbsoluteDate = (date: Date | string, _locale?: SupportedLocale): string => {
  const parsed = typeof date === 'string' ? parseISO(date) : date
  if (!parsed || isNaN(parsed.getTime())) return ''
  return format(parsed, 'yyyy-MM-dd HH:mm:ss')
}

export const getIncludedMonths = (viewDates: Date[]): MonthIdentifier[] => {
  if (!viewDates || viewDates.length === 0) {
    return []
  }

  const monthsMap = new Map<string, MonthIdentifier>()

  viewDates.forEach((date) => {
    const monthKey = format(date, 'yyyy-MM')
    const year = date.getFullYear()
    const month = date.getMonth() + 1

    if (!monthsMap.has(monthKey)) {
      monthsMap.set(monthKey, { year, month })
    }
  })

  return Array.from(monthsMap.values()).sort((a, b) => {
    if (a.year !== b.year) {
      return a.year - b.year
    }
    return a.month - b.month
  })
}

export const getNextQuarterHour = (date: Date): Date => {
  const plus15 = addMinutes(date, 15)

  const minutes = plus15.getMinutes()
  const roundedMinutes = Math.ceil(minutes / 15) * 15

  let nextQuarter = new Date(plus15)

  if (roundedMinutes === 60) {
    // Passe à l'heure suivante si on dépasse 59
    nextQuarter.setHours(nextQuarter.getHours() + 1)
    nextQuarter.setMinutes(0)
  } else {
    nextQuarter.setMinutes(roundedMinutes)
  }
  nextQuarter = setSeconds(nextQuarter, 0)
  nextQuarter = setMilliseconds(nextQuarter, 0)

  return nextQuarter
}

export const computeUTCRangeFromDateTimeValues = (
  dateRange: { start?: DateValue; end?: DateValue },
  timeRange: { from?: string | number | Date; to?: string | number | Date }
): DateTimeRange | null => {
  if (!dateRange.start || !dateRange.end) return null
  const startTime = timeRange.from ? String(timeRange.from) : '00:00'
  const endTime = timeRange.to ? String(timeRange.to) : '23:59'
  return calendarRangeToUTCRange(dateRange.start, dateRange.end, startTime, endTime)
}

export const isISODateString = (value: unknown): boolean => {
  if (!value) return false
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(String(value))
}
